import { useEffect, useState, useRef, useMemo, useCallback, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import PWAInstallPrompt from "./components/PWAInstallPrompt";

// Pages
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import Dashboard from "./pages/Dashboard";
import BooksPage from "./pages/BooksPage";
import BookUpload from "./pages/BookUpload";
import ChapterSelect from "./pages/ChapterSelect";
import TestGenerator from "./pages/TestGenerator";
import QuizGenerator from "./pages/QuizGenerator";
import QuizTake from "./pages/QuizTake";
import TestTake from "./pages/TestTake";
import TestsPage from "./pages/TestsPage";
import QuizzesPage from "./pages/QuizzesPage";
import PricingPage from "./pages/PricingPage";
import PaymentSuccess from "./pages/PaymentSuccess";
import ProfilePage from "./pages/ProfilePage";
import AdminDashboard from "./pages/AdminDashboard";
import ClassroomsPage from "./pages/ClassroomsPage";
import ClassroomDetail from "./pages/ClassroomDetail";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import JoinClassroom from "./pages/JoinClassroom";
import LectureNotesGenerator from "./pages/LectureNotesGenerator";
import LectureNotesPage from "./pages/LectureNotesPage";
import LectureNotesView from "./pages/LectureNotesView";
import VerifyEmail from "./pages/VerifyEmail";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.withCredentials = true;

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Manage static landing visibility based on route
const ManageStaticLanding = () => {
  const { pathname, hash } = useLocation();
  
  useEffect(() => {
    const staticLanding = document.getElementById('landing-static');
    const isHomepage = pathname === '/' && !hash?.includes('session_id=');
    
    if (isHomepage) {
      // Show static landing on homepage
      document.documentElement.classList.remove('app-route');
      if (staticLanding) {
        staticLanding.style.display = 'block';
      }
    } else {
      // Hide static landing for all other routes
      document.documentElement.classList.add('app-route');
      if (staticLanding) {
        staticLanding.style.display = 'none';
      }
    }
  }, [pathname, hash]);
  
  return null;
};

// Create proper React Context
const AuthContextInstance = createContext(null);

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  const checkAuth = async () => {
    const storedToken = localStorage.getItem("token");
    if (storedToken) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${storedToken}` }
        });
        setUser(response.data);
        setToken(storedToken);
      } catch (e) {
        localStorage.removeItem("token");
        setToken(null);
        setUser(null);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = useCallback((userData, authToken) => {
    localStorage.setItem("token", authToken);
    setToken(authToken);
    setUser(userData);
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
    } catch (e) {
      // Ignore logout errors
    }
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  }, [token]);

  const refreshUser = useCallback(async () => {
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
        return response.data;
      } catch (e) {
        console.error("Failed to refresh user:", e);
        return null;
      }
    }
    return null;
  }, [token]);

  const contextValue = useMemo(
    () => ({ user, token, loading, login, logout, refreshUser, setUser }),
    [user, token, loading, login, logout, refreshUser]
  );

  return (
    <AuthContextInstance.Provider value={contextValue}>
      {children}
    </AuthContextInstance.Provider>
  );
};

// Hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContextInstance);
  if (!context) {
    return { user: null, token: null, loading: true, login: () => {}, logout: () => {}, refreshUser: () => {}, setUser: () => {} };
  }
  return context;
};

// Helper function for authenticated API calls
export const getAuthHeaders = (token) => {
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-hero-gradient">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Auth Callback for Google OAuth
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const { login } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await axios.post(`${API}/auth/session`, 
            { session_id: sessionId },
            { withCredentials: true }
          );
          
          if (response.data.user) {
            login(response.data.user, response.data.user.user_id);
            navigate("/dashboard", { replace: true });
          }
        } catch (error) {
          console.error("Auth callback error:", error);
          navigate("/login", { replace: true });
        }
      } else {
        navigate("/login", { replace: true });
      }
    };

    processSession();
  }, [location, navigate, login]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-hero-gradient">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-500 border-t-transparent mx-auto mb-4"></div>
        <p className="text-slate-600">Completing sign in...</p>
      </div>
    </div>
  );
};

// App Router
function AppRouter() {
  const location = useLocation();
  
  // Check for session_id in URL hash (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return (
      <>
        <ManageStaticLanding />
        <AuthCallback />
      </>
    );
  }
  
  // On homepage, show static HTML but still manage the landing visibility
  if (location.pathname === '/') {
    return <ManageStaticLanding />;
  }

  return (
    <>
      <ManageStaticLanding />
      <ScrollToTop />
      <Routes>
        {/* Landing page is now static HTML - no React route for "/" */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        
        {/* Protected Routes */}
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/books" element={<ProtectedRoute><BooksPage /></ProtectedRoute>} />
        <Route path="/books/upload" element={<ProtectedRoute><BookUpload /></ProtectedRoute>} />
        <Route path="/books/:bookId/chapters" element={<ProtectedRoute><ChapterSelect /></ProtectedRoute>} />
        <Route path="/books/:bookId/test" element={<ProtectedRoute><TestGenerator /></ProtectedRoute>} />
        <Route path="/books/:bookId/quiz" element={<ProtectedRoute><QuizGenerator /></ProtectedRoute>} />
        <Route path="/books/:bookId/lecture-notes" element={<ProtectedRoute><LectureNotesGenerator /></ProtectedRoute>} />
        <Route path="/tests" element={<ProtectedRoute><TestsPage /></ProtectedRoute>} />
        <Route path="/tests/:testId/take" element={<ProtectedRoute><TestTake /></ProtectedRoute>} />
        <Route path="/quizzes" element={<ProtectedRoute><QuizzesPage /></ProtectedRoute>} />
        <Route path="/quiz/:quizId/take" element={<ProtectedRoute><QuizTake /></ProtectedRoute>} />
        <Route path="/lecture-notes" element={<ProtectedRoute><LectureNotesPage /></ProtectedRoute>} />
        <Route path="/lecture-notes/:notesId" element={<ProtectedRoute><LectureNotesView /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        <Route path="/classrooms" element={<ProtectedRoute><ClassroomsPage /></ProtectedRoute>} />
        <Route path="/classrooms/:classroomId" element={<ProtectedRoute><ClassroomDetail /></ProtectedRoute>} />
        <Route path="/subscription/success" element={<ProtectedRoute><SubscriptionSuccess /></ProtectedRoute>} />
        
        {/* Public routes */}
        <Route path="/join/:joinCode" element={<JoinClassroom />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
      </Routes>
    </>
  );
}

// Toaster wrapper - only show on non-homepage routes
function ToasterWrapper() {
  const location = useLocation();
  if (location.pathname === '/') {
    return null;
  }
  return <Toaster position="top-right" richColors />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <ToasterWrapper />
        <PWAInstallPrompt />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
